import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Calculator,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  History,
  Plus,
  Trash2,
} from 'lucide-react'

import { loansService, type ValuationComponent } from '@/services/loans'
import { invalidateLoanQueries, LOAN_QUERY_KEYS } from '@/services/loanQueryKeys'

type Props = {
  loanId: string
  /**
   * Lay out to the screen it is given (Underwriting > Model Review >
   * Calculations, under the one-screen rule). Opened, the workbench is four
   * columns of equal height — formulas, the per-deal override, the component
   * valuation, the history — each scrolling its own list inside; stacked, it
   * ran far past a 309px slot at 1366x768. Below xl it stacks as before.
   */
  fill?: boolean
}

type ComponentDraft = {
  id: string | null
  name: string
  assetType: string
  noi: string
  capRate: string
  reason: string
}

const EMPTY_COMPONENT: ComponentDraft = {
  id: null,
  name: '',
  assetType: '',
  noi: '',
  capRate: '',
  reason: '',
}

/** Field keys the reader knows as initials, not words. */
const ACRONYMS: Record<string, string> = {
  noi: 'NOI', dscr: 'DSCR', ltv: 'LTV', ltc: 'LTC', ncf: 'NCF', sf: 'SF', io: 'IO', irr: 'IRR',
}

/** "debt_yield", or the API's camelCased "debtYield", as "Debt Yield". */
function label(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => ACRONYMS[word.toLowerCase()] ?? word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function number(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 }).format(value)
}

function money(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)
}

function message(error: unknown): string {
  if (!error || typeof error !== 'object') return 'Calculation change failed.'
  const response = (error as { response?: { data?: { detail?: string } } }).response
  return response?.data?.detail ?? 'Calculation change failed.'
}

export function CalculationWorkbench({ loanId, fill = false }: Props) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [overrideTarget, setOverrideTarget] = useState('')
  const [overrideValue, setOverrideValue] = useState('')
  const [overrideReason, setOverrideReason] = useState('')
  const [withdrawOverrideId, setWithdrawOverrideId] = useState<string | null>(null)
  const [withdrawReason, setWithdrawReason] = useState('')
  const [component, setComponent] = useState<ComponentDraft>(EMPTY_COMPONENT)
  const [withdrawComponentId, setWithdrawComponentId] = useState<string | null>(null)
  const [componentWithdrawReason, setComponentWithdrawReason] = useState('')

  const calculations = useQuery({
    queryKey: LOAN_QUERY_KEYS.calculations(loanId),
    queryFn: () => loansService.getCalculations(loanId),
    enabled: Boolean(loanId),
    retry: false,
  })

  const refresh = async () => {
    await invalidateLoanQueries(queryClient, loanId)
  }

  const setOverride = useMutation({
    mutationFn: (input: { target: string; value: number; reason: string }) => loansService.setCalculationOverride(
      loanId,
      input.target,
      input.value,
      input.reason,
    ),
    onSuccess: async () => {
      setOverrideValue('')
      setOverrideReason('')
      await refresh()
    },
  })

  const withdrawOverride = useMutation({
    mutationFn: () => loansService.withdrawCalculationOverride(
      loanId,
      withdrawOverrideId!,
      withdrawReason,
    ),
    onSuccess: async () => {
      setWithdrawOverrideId(null)
      setWithdrawReason('')
      await refresh()
    },
  })

  const saveComponent = useMutation({
    mutationFn: () => {
      const input = {
        name: component.name,
        assetType: component.assetType || null,
        noi: Number(component.noi),
        capRate: Number(component.capRate),
        reason: component.reason,
      }
      return component.id
        ? loansService.updateValuationComponent(loanId, component.id, input)
        : loansService.createValuationComponent(loanId, input)
    },
    onSuccess: async () => {
      setComponent(EMPTY_COMPONENT)
      await refresh()
    },
  })

  const withdrawComponent = useMutation({
    mutationFn: () => loansService.withdrawValuationComponent(
      loanId,
      withdrawComponentId!,
      componentWithdrawReason,
    ),
    onSuccess: async () => {
      setWithdrawComponentId(null)
      setComponentWithdrawReason('')
      await refresh()
    },
  })

  const data = calculations.data
  const formulaRows = useMemo(
    () => Object.entries(data?.derived ?? {}).sort(([a], [b]) => a.localeCompare(b)),
    [data?.derived],
  )
  const targetOptions = useMemo(
    () => [...new Set([
      ...formulaRows.map(([target]) => target),
      ...(data?.overrides ?? []).map((item) => item.target),
    ])],
    [data?.overrides, formulaRows],
  )

  // Q5: until the server positively grants this reader the calculation
  // surface, render no override or component control. The DERIVED figures
  // themselves are every reader's (UI-03): an analyst saw "—" where an
  // approver saw "LTV 65% Derived" on the same deal, so the server now serves
  // them to every role and this surface prints them read-only. One line says
  // why the controls are absent — a blank panel reads as a bug, not a permission.
  if (!data?.unlocked) {
    if (formulaRows.length === 0) {
      return (
        <p className="text-[9.5px] text-stone-600" data-testid="calculation-workbench-locked">
          Calculation detail is available to reviewers with the calculation unlock privilege.
        </p>
      )
    }
    return (
      <section className="rounded-lg border border-stone-800 bg-black/25 p-3" data-testid="calculation-workbench-readonly">
        <div className="mb-1.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-stone-500">
          Derived figures · formula and inputs
        </div>
        <div className="grid gap-2 lg:grid-cols-2">
          {formulaRows.map(([target, calculation]) => (
            <article key={target} className="rounded border border-stone-800 bg-stone-950/55 p-2.5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] font-semibold text-stone-200">{label(target)}</div>
                  <div className="mt-0.5 font-mono text-[9px] text-violet-300">{calculation.formula ?? 'Formula unavailable'}</div>
                </div>
                <span className="font-mono text-[11px] text-amber-200">{number(calculation.value)}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {Object.entries(calculation.inputs ?? {}).map(([input, value]) => (
                  <span key={input} className="rounded bg-stone-900 px-1.5 py-0.5 font-mono text-[8px] text-stone-400">
                    {label(input)} = {number(value)}
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>
        <p className="mt-2 text-[9.5px] text-stone-600" data-testid="calculation-workbench-locked">
          Overrides, component valuation and the calculation history are available to reviewers with the calculation unlock privilege.
        </p>
      </section>
    )
  }

  const activeTarget = overrideTarget || targetOptions[0] || ''
  const componentValue = component.noi && component.capRate && Number(component.capRate) > 0
    ? Number(component.noi) / (Number(component.capRate) / 100)
    : null
  const mutationError = setOverride.error
    ?? withdrawOverride.error
    ?? saveComponent.error
    ?? withdrawComponent.error

  // Opened on a screen it fills, the columns take the height under the header.
  const filling = fill && open
  const column = filling ? 'xl:flex xl:min-h-0 xl:flex-col' : ''
  const list = filling ? 'xl:min-h-0 xl:flex-1 xl:overflow-y-auto' : ''

  return (
    <section
      className={`rounded-lg border border-amber-400/20 bg-black/25 ${filling ? 'flex flex-col xl:h-full xl:min-h-0' : ''}`}
      data-testid="calculation-workbench"
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full shrink-0 items-center justify-between gap-3 px-3 py-2.5 text-left"
      >
        <span className="flex items-center gap-2">
          <Calculator size={14} className="text-amber-300" />
          <span>
            <span className="block text-[11px] font-semibold text-amber-100">
              {open ? 'Calculations unlocked' : 'Unlock to see calcs'}
            </span>
            <span className="block text-[9px] text-stone-500">
              Formula inputs, deal exceptions, component valuation and immutable history
            </span>
          </span>
        </span>
        {open ? <ChevronUp size={14} className="text-stone-500" /> : <ChevronDown size={14} className="text-stone-500" />}
      </button>

      {open ? (
        <div
          className={`border-t border-amber-400/10 p-3 ${
            filling ? 'space-y-3 xl:flex xl:min-h-0 xl:flex-1 xl:flex-col xl:gap-2 xl:space-y-0' : 'space-y-3'
          }`}
        >
          {mutationError ? (
            <div className="flex shrink-0 items-start gap-2 rounded border border-red-500/25 bg-red-500/10 px-2.5 py-2 text-[10px] text-red-200">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {message(mutationError)}
            </div>
          ) : null}

          <div
            className={
              filling
                ? 'space-y-3 xl:grid xl:min-h-0 xl:flex-1 xl:grid-cols-4 xl:grid-rows-[minmax(0,1fr)] xl:gap-2 xl:space-y-0'
                : 'space-y-3'
            }
          >
            <div className={`${column} ${filling ? 'xl:rounded xl:border xl:border-stone-800 xl:p-2.5' : ''}`}>
              <div className="mb-1.5 shrink-0 text-[9px] font-semibold uppercase tracking-[0.14em] text-stone-500">
                Formula and inputs
              </div>
              {formulaRows.length === 0 ? (
                // Only values the engine derived carry a formula; a blank
                // section read as a fault. It claims nothing about where the
                // other values came from: most of Calloway's are Suggested,
                // not stated or entered (nd-015) — each row's state says.
                <p className="text-[9.5px] text-stone-500">
                  No figure on this deal is derived by the platform, so none is explained by a formula. Each
                  value's own state — Stated, Suggested or Confirmed — says where it came from.
                </p>
              ) : null}
              <div className={`grid gap-2 lg:grid-cols-2 ${filling ? 'xl:grid-cols-1 xl:content-start' : ''} ${list}`}>
                {formulaRows.map(([target, calculation]) => (
                  <article key={target} className="rounded border border-stone-800 bg-stone-950/55 p-2.5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-[10px] font-semibold text-stone-200">{label(target)}</div>
                        <div className="mt-0.5 font-mono text-[9px] text-violet-300">{calculation.formula ?? 'Formula unavailable'}</div>
                      </div>
                      <span className="font-mono text-[11px] text-amber-200">{number(calculation.value)}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {Object.entries(calculation.inputs).map(([input, value]) => (
                        <span key={input} className="rounded bg-stone-900 px-1.5 py-0.5 font-mono text-[8px] text-stone-400">
                          {label(input)} = {number(value)}
                        </span>
                      ))}
                    </div>
                    <div className={`mt-2 flex items-center gap-1 text-[9px] ${calculation.matchesInputs ? 'text-emerald-400' : 'text-red-300'}`}>
                      {calculation.matchesInputs ? <CheckCircle2 size={10} /> : <AlertTriangle size={10} />}
                      {calculation.matchesInputs
                        ? 'Stored value follows these inputs'
                        : `Stored value differs; inputs produce ${number(calculation.fromInputs)}`}
                    </div>
                  </article>
                ))}
              </div>
            </div>

            {/* The override and the component valuation: two columns of the
                four when filling (the wrapper steps aside), a pair otherwise. */}
            <div className={filling ? 'grid gap-3 xl:contents' : 'grid gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]'}>
              <div className={`rounded border border-stone-800 p-2.5 ${column}`}>
                <div className="shrink-0 text-[10px] font-semibold text-stone-200">Per-deal override</div>
                <div className="mt-0.5 shrink-0 text-[9px] text-stone-500">Global rules stay unchanged; this exception travels with this deal.</div>
                <div className="mt-2 grid shrink-0 gap-2 sm:grid-cols-2">
                  <select
                    value={activeTarget}
                    onChange={(event) => setOverrideTarget(event.target.value)}
                    disabled={targetOptions.length === 0}
                    className="rounded border border-stone-700 bg-stone-950 px-2 py-1.5 text-[10px] text-stone-200 disabled:opacity-50"
                  >
                    {targetOptions.length === 0 ? <option value="">No derived figure to override</option> : null}
                    {targetOptions.map((target) => <option key={target} value={target}>{label(target)}</option>)}
                  </select>
                  <input
                    type="number"
                    value={overrideValue}
                    onChange={(event) => setOverrideValue(event.target.value)}
                    placeholder="Override value"
                    className="rounded border border-stone-700 bg-stone-950 px-2 py-1.5 text-[10px] text-stone-200"
                  />
                </div>
                <textarea
                  value={overrideReason}
                  onChange={(event) => setOverrideReason(event.target.value)}
                  placeholder="Why is this deal exceptional?"
                  className="mt-2 min-h-14 w-full shrink-0 rounded border border-stone-700 bg-stone-950 px-2 py-1.5 text-[10px] text-stone-200"
                />
                <button
                  type="button"
                  disabled={!activeTarget || !overrideValue || overrideReason.trim().length < 3 || setOverride.isPending}
                  onClick={() => {
                    setOverride.mutate({
                      target: activeTarget,
                      value: Number(overrideValue),
                      reason: overrideReason,
                    })
                  }}
                  className="mt-2 shrink-0 self-start rounded border border-amber-300/30 bg-amber-300/10 px-2.5 py-1.5 text-[10px] text-amber-100 disabled:opacity-40"
                >
                  Record override
                </button>

                <div className={list}>
                  {(data.overrides ?? []).map((override) => (
                    <div key={override.id} className="mt-2 rounded border border-amber-300/15 bg-amber-300/[0.05] p-2 text-[9px]">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-semibold text-amber-100">{label(override.target)}</div>
                          <div className="font-mono text-stone-400">Engine {number(override.engineValue)} → Override {number(override.value)}</div>
                          <div className="mt-1 text-stone-500">{override.createdBy} · {new Date(override.createdAt).toLocaleString()}</div>
                          <div className="mt-1 text-stone-300">{override.reason}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setWithdrawOverrideId(override.id)}
                          className="rounded p-1 text-stone-500 hover:bg-red-500/10 hover:text-red-300"
                          aria-label={`Withdraw ${label(override.target)} override`}
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                      {withdrawOverrideId === override.id ? (
                        <div className="mt-2 flex gap-1.5">
                          <input
                            value={withdrawReason}
                            onChange={(event) => setWithdrawReason(event.target.value)}
                            placeholder="Reason for withdrawal"
                            className="min-w-0 flex-1 rounded border border-stone-700 bg-stone-950 px-2 py-1 text-[9px] text-stone-200"
                          />
                          <button
                            type="button"
                            disabled={withdrawReason.trim().length < 3 || withdrawOverride.isPending}
                            onClick={() => withdrawOverride.mutate()}
                            className="rounded border border-red-400/25 px-2 py-1 text-red-200 disabled:opacity-40"
                          >
                            Confirm
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>

              <div className={`rounded border border-stone-800 p-2.5 ${column}`}>
                <div className="flex shrink-0 items-start justify-between gap-2">
                  <div>
                    <div className="text-[10px] font-semibold text-stone-200">Per-component valuation</div>
                    <div className="mt-0.5 text-[9px] text-stone-500">Each business carries isolated NOI and cap rate; values combine for deal LTV.</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[8px] uppercase tracking-wide text-stone-600">Combined value</div>
                    <div className="font-mono text-[12px] text-amber-200">{money(data.componentValuation?.value)}</div>
                  </div>
                </div>

                <div className="mt-2 grid shrink-0 gap-1.5 sm:grid-cols-2">
                  <input value={component.name} onChange={(event) => setComponent((value) => ({ ...value, name: event.target.value }))} placeholder="Component name" className="rounded border border-stone-700 bg-stone-950 px-2 py-1.5 text-[10px] text-stone-200" />
                  <input value={component.assetType} onChange={(event) => setComponent((value) => ({ ...value, assetType: event.target.value }))} placeholder="Asset type" className="rounded border border-stone-700 bg-stone-950 px-2 py-1.5 text-[10px] text-stone-200" />
                  <input type="number" value={component.noi} onChange={(event) => setComponent((value) => ({ ...value, noi: event.target.value }))} placeholder="NOI" className="rounded border border-stone-700 bg-stone-950 px-2 py-1.5 text-[10px] text-stone-200" />
                  <input type="number" value={component.capRate} onChange={(event) => setComponent((value) => ({ ...value, capRate: event.target.value }))} placeholder="Cap rate %" className="rounded border border-stone-700 bg-stone-950 px-2 py-1.5 text-[10px] text-stone-200" />
                </div>
                <input value={component.reason} onChange={(event) => setComponent((value) => ({ ...value, reason: event.target.value }))} placeholder="Reason for this component decision" className="mt-1.5 w-full shrink-0 rounded border border-stone-700 bg-stone-950 px-2 py-1.5 text-[10px] text-stone-200" />
                <div className="mt-1.5 flex shrink-0 items-center justify-between">
                  <span className="font-mono text-[9px] text-stone-500">NOI / cap = {money(componentValue)}</span>
                  <div className="flex gap-1.5">
                    {component.id ? (
                      <button type="button" onClick={() => setComponent(EMPTY_COMPONENT)} className="rounded px-2 py-1 text-[9px] text-stone-500">Cancel</button>
                    ) : null}
                    <button
                      type="button"
                      disabled={!component.name.trim() || !component.noi || !component.capRate || component.reason.trim().length < 3 || saveComponent.isPending}
                      onClick={() => saveComponent.mutate()}
                      className="inline-flex items-center gap-1 rounded border border-amber-300/30 bg-amber-300/10 px-2 py-1 text-[9px] text-amber-100 disabled:opacity-40"
                    >
                      <Plus size={10} /> {component.id ? 'Update component' : 'Add component'}
                    </button>
                  </div>
                </div>

                <div className={list}>
                  {(data.componentValuation?.components ?? []).map((item: ValuationComponent) => (
                    <div key={item.id} className="mt-2 rounded border border-stone-800 bg-stone-950/40 p-2 text-[9px]">
                      <div className="flex items-start justify-between gap-2">
                        <button
                          type="button"
                          onClick={() => setComponent({
                            id: item.id,
                            name: item.name,
                            assetType: item.assetType ?? '',
                            noi: String(item.noi),
                            capRate: String(item.capRate),
                            reason: '',
                          })}
                          className="min-w-0 text-left"
                        >
                          <span className="block font-semibold text-stone-200">{item.name} <span className="font-normal text-stone-500">{item.assetType}</span></span>
                          <span className="mt-0.5 block font-mono text-stone-400">{money(item.noi)} NOI ÷ {number(item.capRate)}% = {money(item.value)}</span>
                        </button>
                        <button type="button" onClick={() => setWithdrawComponentId(item.id)} className="rounded p-1 text-stone-500 hover:bg-red-500/10 hover:text-red-300" aria-label={`Withdraw ${item.name} component`}><Trash2 size={11} /></button>
                      </div>
                      {withdrawComponentId === item.id ? (
                        <div className="mt-2 flex gap-1.5">
                          <input value={componentWithdrawReason} onChange={(event) => setComponentWithdrawReason(event.target.value)} placeholder="Reason for withdrawal" className="min-w-0 flex-1 rounded border border-stone-700 bg-stone-950 px-2 py-1 text-[9px] text-stone-200" />
                          <button type="button" disabled={componentWithdrawReason.trim().length < 3 || withdrawComponent.isPending} onClick={() => withdrawComponent.mutate()} className="rounded border border-red-400/25 px-2 py-1 text-red-200 disabled:opacity-40">Confirm</button>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className={`rounded border border-stone-800 p-2.5 ${column}`}>
              <div className="flex shrink-0 items-center gap-1.5 text-[10px] font-semibold text-stone-200"><History size={11} /> Immutable calculation history</div>
              {(data.audit ?? []).length === 0 ? (
                // Said, not left blank: an empty box read as a history that failed to load.
                <p className="mt-2 text-[9.5px] text-stone-600">No override or component decision is recorded on this deal yet.</p>
              ) : null}
              <div className={`mt-2 grid gap-1.5 lg:grid-cols-2 ${filling ? 'xl:grid-cols-1 xl:content-start' : ''} ${list}`}>
                {(data.audit ?? []).slice(0, 12).map((event) => (
                  <div key={event.id} className="rounded bg-stone-950/60 px-2 py-1.5 text-[8px] text-stone-400">
                    <div className="flex justify-between gap-2"><span className="font-semibold text-stone-300">{label(event.eventType)}</span><span>{new Date(event.createdAt).toLocaleString()}</span></div>
                    <div className="mt-0.5">{event.actor} · {event.reason}</div>
                    <div className="mt-0.5 truncate font-mono text-stone-600" title={event.eventHash}>hash {event.eventHash}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  )
}
